/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/store/attendance.ts
 * PURPOSE: State management and data store
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: GPS-validated attendance with Vietnamese Labor Law compliance
 *
 * @business-context
 * - Domain: State Management, Global State, Vietnamese F&B Application State
 * - Impact: State Consistency, Performance, Developer Experience
 * - Stakeholders: Development Team, Frontend Engineers
 *
 * @technical-architecture
 * - Layer: State Management Layer, Global Store
 * - Pattern: Redux/Zustand Pattern, State Slices, Actions
 * - Dependencies: Redux Toolkit/Zustand, TypeScript, Immer
 */

import { create } from "zustand";
import { AttendanceStatus, GeoLocation } from "../types/attendance";

interface AttendanceState {
  // State
  status: AttendanceStatus | null;
  currentLocation: GeoLocation | null;
  isLocationValid: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setStatus: (status: AttendanceStatus) => void;
  setCurrentLocation: (location: GeoLocation | null) => void;
  setLocationValid: (isValid: boolean) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

// useAttendanceStore__processes_logic__returns_result
export const useAttendanceStore = create<AttendanceState>((set) => ({
  // Initial state
  status: null,
  currentLocation: null,
  isLocationValid: true,
  isLoading: false,
  error: null,

  // Actions
  setStatus: (status) =>
    set((state) => {
      // Force a complete state update to trigger re-renders
      return {
        ...state,
        status: { ...status }, // Create new object reference
        error: null,
        isLoading: false,
      };
    }),

  setCurrentLocation: (currentLocation) => set({ currentLocation }),

  setLocationValid: (isLocationValid) => set({ isLocationValid }),

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error, isLoading: false }),

  reset: () =>
    set({
      status: null,
      currentLocation: null,
      isLocationValid: true,
      isLoading: false,
      error: null,
    }),
}));
